<!--
 * 智能POS 云MIS 小型收银台（H5，供店铺员工使用）
 *
 * 进入方式（链接风格同 /hub/:token）：
 * 1) 商户端「收款」入口生成带 token 的链接/二维码，员工扫码进入：
 *    /mis-cashier/<商户JWT>?mchName=<可选,店铺名>&deviceNo=<可选,智能POS编号>
 *    （兼容旧格式 /mis-cashier?token=<...>）
 * 2) 老板首次用带 token 链接进入后，可点右上角二维码，展示/打印本收银台二维码，
 *    店员用微信/相机扫码即可反复进入（token 随二维码携带）。
 *
 * 流程：输入金额 -> 选择刷卡(银行卡消费)/扫码 -> 确认收款，推送到POS终端完成收款。
-->
<template>
  <div class="mis-cashier">
    <ConfirmModal ref="confirmModal"></ConfirmModal>

    <!-- token 缺失：提示错误 -->
    <div v-if="!hasToken" class="mis-guard">
      <div class="mis-guard-card">
        <img class="mis-guard-icon" src="../assets/icon/tip.svg" alt="" />
        <div class="mis-guard-title">无法进入收银台</div>
        <div class="mis-guard-msg">请扫描商户端「收款」入口生成的收银台二维码进入</div>
      </div>
    </div>

    <template v-else>
      <!-- 顶部 Hero -->
      <div class="mis-hero">
        <div class="mis-hero-glow"></div>
        <div class="mis-hero-top">
          <div class="mis-hero-store">
            <div class="mis-hero-avatar">{{ storeInitial }}</div>
            <div class="mis-hero-meta">
              <div class="mis-hero-name">{{ mchName || '店铺收银台' }}</div>
              <div class="mis-hero-sub">云MIS · 智能POS收款</div>
            </div>
          </div>
          <div class="mis-hero-qr" @click="openQr">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
              <rect x="3" y="3" width="7" height="7" rx="1.2" />
              <rect x="14" y="3" width="7" height="7" rx="1.2" />
              <rect x="3" y="14" width="7" height="7" rx="1.2" />
              <line x1="14" y1="14" x2="14" y2="17.5" />
              <line x1="17.5" y1="14" x2="21" y2="14" />
              <line x1="21" y1="14" x2="21" y2="21" />
              <line x1="14" y1="21" x2="17.5" y2="21" />
              <line x1="17.5" y1="17.5" x2="17.5" y2="21" />
            </svg>
          </div>
        </div>
        <div class="mis-hero-device" @click="deviceLocked ? null : openDeviceSelector()">
          <span class="mis-dot"></span>
          <span class="mis-hero-device-text">
            {{ selectedDevice ? deviceLabel(selectedDevice) : (deviceLoading ? '加载设备中...' : '未指定设备') }}
          </span>
          <span class="mis-hero-device-switch" v-if="deviceLocked">已指定</span>
          <span class="mis-hero-device-switch" v-else>切换 ›</span>
        </div>
      </div>

      <!-- 金额卡片（浮起，压住 hero 底部）-->
      <div class="mis-amount-card">
        <div class="mis-amount-label">收款金额</div>
        <div class="mis-amount-row">
          <span class="mis-amount-symbol">¥</span>
          <span class="mis-amount-value" :class="{ placeholder: !amountDisplay }">{{ amountViewText }}</span>
          <span class="mis-cursor" v-if="amountDisplay"></span>
        </div>
      </div>

      <!-- 交易方式：刷卡 / 扫码 -->
      <div class="mis-trade">
        <div class="mis-trade-grid">
          <div
            v-for="t in tradeTypes"
            :key="t.value"
            class="mis-trade-item"
            :class="{ active: activeTradeType === t.value }"
            :style="activeTradeType === t.value ? { '--accent': t.color } : {}"
            @click="activeTradeType = t.value"
          >
            <div class="mis-trade-icon" :style="{ background: activeTradeType === t.value ? t.color : t.softColor, color: activeTradeType === t.value ? '#fff' : t.color }" v-html="t.svg"></div>
            <div class="mis-trade-text">
              <div class="mis-trade-name">{{ t.label }}</div>
              <div class="mis-trade-desc">{{ t.desc }}</div>
            </div>
            <div class="mis-trade-check" :style="{ borderColor: activeTradeType === t.value ? t.color : '#d5d8e0' }">
              <span v-if="activeTradeType === t.value" class="mis-trade-check-dot" :style="{ background: t.color }"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 快捷金额 -->
      <div class="mis-quick">
        <div class="mis-quick-chip" v-for="q in quickAmounts" :key="q" @click="addQuickAmount(q)">
          <span class="mis-quick-plus">+</span>{{ q }}
        </div>
        <div class="mis-quick-chip mis-quick-clear" @click="amountRaw = ''">清空</div>
      </div>

      <!-- 数字键盘 -->
      <div class="mis-numpad">
        <div class="mis-numpad-row" v-for="(row, ri) in numpadRows" :key="ri">
          <div
            v-for="(key, ci) in row"
            :key="ci"
            class="mis-numpad-key"
            :class="{ 'is-del': key === 'del', 'is-dot': key === '.' }"
            @click="handleKey(key)"
          >
            <template v-if="key === 'del'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                <line x1="18" y1="9" x2="12" y2="15" />
                <line x1="12" y1="9" x2="18" y2="15" />
              </svg>
            </template>
            <template v-else>{{ key }}</template>
          </div>
        </div>
      </div>

      <!-- 确认收款 -->
      <div class="mis-confirm">
        <div class="mis-confirm-btn" :class="{ disabled: !canConfirm }" @click="handleConfirm">
          <span v-if="canConfirm" class="mis-confirm-amt">收款 ¥{{ amountViewText }}</span>
          <span v-else>{{ confirmHint }}</span>
        </div>
      </div>

      <!-- 设备选择弹层 -->
      <transition name="mis-fade">
        <div v-if="showDeviceSelector" class="mis-sheet-mask">
          <div class="mis-sheet-backdrop" @click="closeDeviceSelector"></div>
          <div class="mis-sheet">
            <div class="mis-sheet-grip"></div>
            <div class="mis-sheet-header">
              <span>选择智能POS设备</span>
              <span class="mis-sheet-close" @click="closeDeviceSelector">×</span>
            </div>
            <div v-if="deviceLoading" class="mis-sheet-tip">加载中...</div>
            <div v-else-if="smartPosDevices.length === 0" class="mis-sheet-tip">暂无智能POS设备</div>
            <div v-else class="mis-sheet-list">
              <div class="mis-sheet-item" :class="{ active: !tempDevice }" @click="tempDevice = null">
                <div class="mis-sheet-item-main">
                  <div class="mis-sheet-item-name">不指定设备</div>
                  <div class="mis-sheet-item-sub">由云MIS默认终端收款</div>
                </div>
                <div class="mis-sheet-item-radio" :class="{ on: !tempDevice }"></div>
              </div>
              <div
                v-for="(d, i) in smartPosDevices"
                :key="i"
                class="mis-sheet-item"
                :class="{ active: tempDevice && tempDevice.deviceNo === d.deviceNo }"
                @click="tempDevice = d"
              >
                <div class="mis-sheet-item-main">
                  <div class="mis-sheet-item-name">{{ d.deviceName || d.deviceNo }}</div>
                  <div class="mis-sheet-item-sub">{{ d.deviceNo }}<template v-if="d.storeName"> · {{ d.storeName }}</template></div>
                </div>
                <div class="mis-sheet-item-radio" :class="{ on: tempDevice && tempDevice.deviceNo === d.deviceNo }"></div>
              </div>
            </div>
            <div class="mis-sheet-footer">
              <div class="mis-sheet-confirm" @click="confirmDeviceSelection">确认</div>
            </div>
          </div>
        </div>
      </transition>

      <!-- 收银台二维码弹层 -->
      <transition name="mis-fade">
        <div v-if="showQr" class="mis-qr-mask" @click.self="showQr = false">
          <div class="mis-qr-card">
            <div class="mis-qr-title">收银台二维码</div>
            <div class="mis-qr-sub">店员用微信 / 相机扫码进入收银台</div>
            <div class="mis-qr-store">{{ mchName || '店铺收银台' }}</div>
            <div class="mis-qr-img-wrap">
              <img class="mis-qr-img" :src="qrImgUrl" alt="收银台二维码" />
            </div>
            <div class="mis-qr-hint">扫码即可开始收款 · 无需登录</div>
            <div class="mis-qr-close" @click="showQr = false">关闭</div>
          </div>
        </div>
      </transition>

      <!-- 收款结果轮询遮罩 -->
      <transition name="mis-fade">
        <div v-if="resultOverlay.show" class="mis-result-mask">
          <div class="mis-result-box">
            <template v-if="resultOverlay.status === 'waiting'">
              <div class="mis-result-spinner"></div>
              <div class="mis-result-title">已推送到POS终端</div>
              <div class="mis-result-desc">请在POS上完成收款，正在等待结果...</div>
              <div class="mis-result-amount">¥{{ resultOverlay.amount }}</div>
              <div class="mis-result-btn ghost" @click="closeResult">收款完成 / 继续</div>
            </template>
            <template v-else-if="resultOverlay.status === 'success'">
              <div class="mis-result-icon success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div class="mis-result-title">收款成功</div>
              <div class="mis-result-amount">¥{{ resultOverlay.amount }}</div>
              <div class="mis-result-btn" @click="closeResult">继续收款</div>
            </template>
            <template v-else-if="resultOverlay.status === 'fail'">
              <div class="mis-result-icon fail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <div class="mis-result-title">收款失败</div>
              <div class="mis-result-desc">{{ resultOverlay.msg || '交易未完成' }}</div>
              <div class="mis-result-btn" @click="closeResult">重新收款</div>
            </template>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { pushMisOrder, getMchAllDevices, queryMisOrder, getCashierContext, setMisToken, getMisToken } from '@/api/mis'

const TRADE_TYPES = [
  {
    value: 1,
    label: '刷卡',
    desc: '银行卡消费',
    color: '#2F54EB',
    softColor: '#eaefff',
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="5" width="20" height="14" rx="2.4"/><line x1="2" y1="9.5" x2="22" y2="9.5"/><line x1="6" y1="15" x2="10" y2="15"/></svg>'
  },
  {
    value: 3,
    label: '扫码',
    desc: '微信/支付宝/云闪付',
    color: '#12b76a',
    softColor: '#e6f7ef',
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1.2"/><rect x="14" y="3" width="7" height="7" rx="1.2"/><rect x="3" y="14" width="7" height="7" rx="1.2"/><line x1="14" y1="14" x2="14" y2="21"/><line x1="18" y1="14" x2="18" y2="18"/><line x1="21" y1="14" x2="21" y2="21"/></svg>'
  }
]

export default {
  name: 'MisCashier',
  data () {
    return {
      hasToken: false,
      mchName: '',
      amountRaw: '',
      tradeTypes: TRADE_TYPES,
      activeTradeType: 1,
      numpadRows: [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['.', '0', 'del']
      ],
      quickAmounts: [10, 50, 100, 200],
      // 设备
      deviceLoading: false,
      allDevices: [],
      selectedDevice: null,
      showDeviceSelector: false,
      tempDevice: null,
      preferredDeviceNo: '',
      preferredDeviceName: '',
      deviceLocked: false,
      // 二维码
      showQr: false,
      // 提交/轮询
      submitting: false,
      resultOverlay: {
        show: false,
        status: 'waiting', // waiting | success | fail
        amount: '',
        msg: '',
        payOrderId: ''
      },
      pollTimer: null,
      pollCount: 0
    }
  },
  computed: {
    storeInitial () {
      const name = (this.mchName || '店').trim()
      return name.charAt(0) || '店'
    },
    amountDisplay () {
      const raw = this.amountRaw
      if (raw === null || raw === undefined || raw === 'undefined') return ''
      return String(raw)
    },
    amountViewText () {
      return this.amountDisplay || '0.00'
    },
    amountInFen () {
      const num = parseFloat(this.amountDisplay)
      if (isNaN(num) || num <= 0) return 0
      return Math.round(num * 100)
    },
    smartPosDevices () {
      return this.allDevices.filter(d => d.deviceType === 'smart_pos')
    },
    canConfirm () {
      return this.amountInFen > 0 && !!this.activeTradeType && !this.submitting
    },
    confirmHint () {
      if (this.amountInFen <= 0) return '请输入收款金额'
      if (!this.activeTradeType) return '请选择交易方式'
      return '请输入金额'
    },
    cashierUrl () {
      const token = getMisToken()
      // 生成与 /hub/:token 一致风格的干净链接：https://域名/mis-cashier/<token>
      const base = window.location.origin + '/mis-cashier/' + encodeURIComponent(token || '')
      const query = []
      if (this.mchName) query.push('mchName=' + encodeURIComponent(this.mchName))
      if (this.selectedDevice && this.selectedDevice.deviceNo) {
        query.push('deviceNo=' + encodeURIComponent(this.selectedDevice.deviceNo))
      }
      return base + (query.length ? '?' + query.join('&') : '')
    },
    qrImgUrl () {
      const data = encodeURIComponent(this.cashierUrl)
      return 'https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&data=' + data
    }
  },
  created () {
    const route = this.$route || {}
    const params = route.params || {}
    const q = route.query || {}
    // token 优先取路径参数（/mis-cashier/:token，风格同 /hub/:token），兼容旧的 ?token=
    const token = params.token || q.token
    if (token) setMisToken(token)
    this.hasToken = !!getMisToken()
    if (q.mchName) {
      try { this.mchName = decodeURIComponent(q.mchName) } catch (e) { this.mchName = q.mchName }
    }
    if (q.deviceNo) this.preferredDeviceNo = String(q.deviceNo).trim()
    if (q.deviceName) {
      try { this.preferredDeviceName = decodeURIComponent(q.deviceName) } catch (e) { this.preferredDeviceName = q.deviceName }
    }
    // 链接已在生成二维码时绑定设备：进入即锁定并展示，无需等待设备列表
    if (this.preferredDeviceNo) {
      this.deviceLocked = true
      this.selectedDevice = {
        deviceType: 'smart_pos',
        deviceNo: this.preferredDeviceNo,
        deviceName: this.preferredDeviceName || this.preferredDeviceNo,
        appId: ''
      }
    }
  },
  mounted () {
    if (this.hasToken) {
      this.fetchContext()
      this.fetchDevices()
    }
  },
  beforeDestroy () {
    this.stopPoll()
  },
  methods: {
    deviceLabel (d) {
      if (!d) return ''
      return d.deviceName || d.deviceNo
    },

    openQr () {
      this.showQr = true
    },

    async fetchContext () {
      try {
        const ctx = await getCashierContext()
        // URL 未显式带 mchName 时，用服务端返回的店铺名
        if (ctx && ctx.mchName && !this.mchName) {
          this.mchName = ctx.mchName
        }
        // 服务端 token 绑定的设备为权威来源：锁定并展示，保证收款固定走该POS
        if (ctx && ctx.deviceNo) {
          this.preferredDeviceNo = String(ctx.deviceNo).trim()
          this.deviceLocked = true
          this.selectedDevice = {
            deviceType: 'smart_pos',
            deviceNo: ctx.deviceNo,
            deviceName: ctx.deviceName || this.preferredDeviceName || ctx.deviceNo,
            appId: (this.selectedDevice && this.selectedDevice.appId) || ''
          }
        }
      } catch (e) {
        if (e && (e.code === 401 || e.code === 1001)) {
          this.hasToken = false
        } else if (e && e.msg && /失效|无效/.test(e.msg)) {
          // token 无效或过期
          this.hasToken = false
        }
      }
    },

    normalizeDevice (item) {
      if (!item) return null
      const deviceType = item.deviceType || item.DeviceType || ''
      const deviceNo = item.deviceNo || item.DeviceNo || item.plateNo || item.PlateNo || ''
      const deviceName = item.deviceName || item.DeviceName || item.plateName || item.PlateName || deviceNo
      return {
        deviceType,
        deviceNo,
        deviceName,
        appId: item.appId || item.AppId || '',
        storeName: item.storeName || item.StoreName || item.bindStoreName || item.BindStoreName || ''
      }
    },

    async fetchDevices () {
      this.deviceLoading = true
      try {
        const res = await getMchAllDevices()
        // 兼容多种返回结构：{items} / {data:{items}} / 直接数组
        let items = []
        if (res && Array.isArray(res.items)) items = res.items
        else if (res && res.data && Array.isArray(res.data.items)) items = res.data.items
        else if (Array.isArray(res)) items = res
        const mapped = items.map(i => this.normalizeDevice(i)).filter(Boolean)
        this.allDevices = mapped
        // 直接基于本次结果计算，避免 computed 缓存时序问题
        const smartPos = mapped.filter(d => d.deviceType === 'smart_pos')
        let picked = null
        if (this.preferredDeviceNo) {
          picked = smartPos.find(d => d.deviceNo === this.preferredDeviceNo) || null
        }
        // 链接已绑定设备（deviceLocked）：优先补全设备信息（如 appId），但不改变已锁定选择
        if (this.deviceLocked) {
          if (picked) this.selectedDevice = picked
          // 未在列表中命中时，保留 created/context 构造的锁定设备，不覆盖
        } else {
          if (!picked && smartPos.length > 0) picked = smartPos[0]
          this.selectedDevice = picked
        }
      } catch (e) {
        this.allDevices = []
        if (e && (e.code === 401 || e.code === 1001)) {
          this.hasToken = false
        } else if (e && e.msg && /失效|无效/.test(e.msg)) {
          this.hasToken = false
        }
      } finally {
        this.deviceLoading = false
      }
    },

    openDeviceSelector () {
      this.tempDevice = this.selectedDevice
      this.showDeviceSelector = true
      if (this.allDevices.length === 0 && !this.deviceLoading) {
        this.fetchDevices()
      }
    },

    closeDeviceSelector () {
      this.showDeviceSelector = false
    },

    confirmDeviceSelection () {
      this.selectedDevice = this.tempDevice
      this.showDeviceSelector = false
    },

    addQuickAmount (v) {
      const cur = parseFloat(this.amountDisplay) || 0
      let next = cur + v
      if (next > 999999) next = 999999
      // 有小数则保留两位，否则整数展示
      this.amountRaw = Number.isInteger(next) ? String(next) : next.toFixed(2)
    },

    handleKey (key) {
      if (!key) return
      let raw = this.amountDisplay
      if (key === 'del') {
        this.amountRaw = raw.slice(0, -1)
        return
      }
      if (key === '.' && raw.includes('.')) return
      const dotIdx = raw.indexOf('.')
      if (dotIdx !== -1 && raw.length - dotIdx > 2) return
      if (raw.replace('.', '').length >= 8) return
      if (raw === '' && key === '0') { this.amountRaw = '0'; return }
      if (raw === '' && key === '.') { this.amountRaw = '0.'; return }
      if (raw === '0' && key !== '.') { this.amountRaw = key; return }
      this.amountRaw = raw + key
    },

    async handleConfirm () {
      if (!this.canConfirm) return
      const tradeItem = this.tradeTypes.find(t => t.value === this.activeTradeType)
      const amountYuan = (this.amountInFen / 100).toFixed(2)
      this.submitting = true
      try {
        const res = await pushMisOrder({
          tradeType: this.activeTradeType,
          amount: amountYuan,
          deviceNo: this.selectedDevice ? this.selectedDevice.deviceNo : '',
          appId: this.selectedDevice ? this.selectedDevice.appId : '',
          orderInfo: (tradeItem ? tradeItem.desc : '云MIS') + '收款'
        })
        this.submitting = false
        this.resultOverlay = {
          show: true,
          status: 'waiting',
          amount: amountYuan,
          msg: '',
          payOrderId: (res && res.payOrderId) || ''
        }
        if (this.resultOverlay.payOrderId) {
          this.startPoll(this.resultOverlay.payOrderId)
        }
      } catch (e) {
        this.submitting = false
        if (e && (e.code === 401 || e.code === 1001)) {
          this.hasToken = false
          return
        }
        this.$confirm({
          title: '推送失败',
          content: (e && e.msg) || '收款推送失败，请重试',
          typeColor: '#2F54EB'
        })
      }
    },

    startPoll (payOrderId) {
      this.stopPoll()
      this.pollCount = 0
      const tick = async () => {
        this.pollCount++
        try {
          const order = await queryMisOrder(payOrderId)
          const state = order && (order.state !== undefined ? order.state : order.State)
          if (state === 2) {
            this.resultOverlay.status = 'success'
            this.stopPoll()
            this.amountRaw = ''
            return
          }
          if (state === 3) {
            this.resultOverlay.status = 'fail'
            this.resultOverlay.msg = (order && (order.errMsg || order.ErrMsg)) || '交易失败'
            this.stopPoll()
            return
          }
          if (state === 6) {
            this.resultOverlay.status = 'fail'
            this.resultOverlay.msg = '订单已关闭'
            this.stopPoll()
            return
          }
        } catch (e) {
          // 查询异常忽略，继续轮询
        }
        if (this.pollCount >= 30) {
          this.stopPoll()
        }
      }
      this.pollTimer = setInterval(tick, 3000)
    },

    stopPoll () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    closeResult () {
      this.stopPoll()
      if (this.resultOverlay.status !== 'fail') {
        this.amountRaw = ''
      }
      this.resultOverlay.show = false
    }
  }
}
</script>

<style lang="css" scoped>
.mis-cashier {
  min-height: 100vh;
  background: #eef1f7;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #1f2329;
  text-align: left;
  padding-bottom: calc(1.7rem + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

/* token 缺失提示 */
.mis-guard {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.6rem;
}
.mis-guard-card {
  width: 100%;
  max-width: 7rem;
  background: #fff;
  border-radius: 0.28rem;
  padding: 0.8rem 0.5rem;
  text-align: center;
  box-shadow: 0 0.1rem 0.5rem rgba(31, 45, 61, 0.08);
}
.mis-guard-icon { width: 1.5rem; margin-bottom: 0.36rem; }
.mis-guard-title { font-size: 0.4rem; font-weight: 700; color: #1f2329; margin-bottom: 0.18rem; }
.mis-guard-msg { font-size: 0.3rem; color: #8a9099; line-height: 1.7; }

/* 顶部 Hero */
.mis-hero {
  position: relative;
  overflow: hidden;
  padding: 0.56rem 0.44rem 0.94rem;
  background: linear-gradient(150deg, #5a7bff 0%, #3355ec 48%, #2036b8 100%);
  color: #fff;
  border-radius: 0 0 0.5rem 0.5rem;
  box-shadow: 0 0.14rem 0.4rem rgba(32, 54, 184, 0.28);
}
.mis-hero::after {
  content: '';
  position: absolute;
  left: -0.8rem;
  bottom: -1.6rem;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 72%);
}
.mis-hero-glow {
  position: absolute;
  top: -1.5rem;
  right: -1.1rem;
  width: 3.8rem;
  height: 3.8rem;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.26) 0%, rgba(255, 255, 255, 0) 70%);
}
.mis-hero-top {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mis-hero-store { display: flex; align-items: center; min-width: 0; flex: 1; }
.mis-hero-avatar {
  width: 0.84rem;
  height: 0.84rem;
  border-radius: 0.22rem;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.42rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-right: 0.24rem;
}
.mis-hero-meta { min-width: 0; }
.mis-hero-name {
  font-size: 0.4rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mis-hero-sub { margin-top: 0.06rem; font-size: 0.24rem; color: rgba(255, 255, 255, 0.75); }
.mis-hero-qr {
  flex-shrink: 0;
  width: 0.78rem;
  height: 0.78rem;
  border-radius: 0.2rem;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.mis-hero-qr:active { background: rgba(255, 255, 255, 0.28); }
.mis-hero-qr svg { width: 0.44rem; height: 0.44rem; }
.mis-hero-device {
  position: relative;
  margin-top: 0.34rem;
  display: flex;
  align-items: center;
  padding: 0.16rem 0.26rem;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  font-size: 0.26rem;
}
.mis-hero-device:active { background: rgba(255, 255, 255, 0.22); }
.mis-hero-device-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.95);
}
.mis-hero-device-switch { flex-shrink: 0; margin-left: 0.16rem; color: rgba(255, 255, 255, 0.8); }
.mis-dot {
  width: 0.14rem;
  height: 0.14rem;
  border-radius: 50%;
  background: #52ffab;
  margin-right: 0.16rem;
  flex-shrink: 0;
  box-shadow: 0 0 0.1rem rgba(82, 255, 171, 0.9);
}

/* 金额卡片 */
.mis-amount-card {
  position: relative;
  margin: -0.58rem 0.32rem 0;
  background: #fff;
  padding: 0.42rem 0.48rem 0.44rem;
  border-radius: 0.3rem;
  box-shadow: 0 0.14rem 0.44rem rgba(31, 45, 61, 0.12);
  overflow: hidden;
}
.mis-amount-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 0.06rem;
  background: linear-gradient(90deg, #5b7bff 0%, #12b76a 100%);
}
.mis-amount-label {
  font-size: 0.26rem;
  color: #9aa0a8;
  margin-bottom: 0.16rem;
  letter-spacing: 0.02rem;
}
.mis-amount-row { display: flex; align-items: flex-end; min-height: 0.96rem; }
.mis-amount-symbol { font-size: 0.5rem; font-weight: 700; color: #1f2329; line-height: 1; padding-bottom: 0.08rem; }
.mis-amount-value {
  font-size: 1.02rem;
  font-weight: 800;
  color: #1f2329;
  line-height: 1;
  margin-left: 0.12rem;
  letter-spacing: -0.02rem;
  font-variant-numeric: tabular-nums;
}
.mis-amount-value.placeholder { color: #cdd1da; font-weight: 600; }
.mis-cursor {
  width: 0.05rem;
  height: 0.68rem;
  background: #2F54EB;
  margin-left: 0.08rem;
  align-self: center;
  border-radius: 0.03rem;
  animation: mis-blink 1s step-end infinite;
}
@keyframes mis-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

/* 交易方式 */
.mis-trade {
  margin: 0.28rem 0.32rem 0;
}
.mis-trade-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.24rem; }
.mis-trade-item {
  position: relative;
  background: #fff;
  border: 0.03rem solid transparent;
  border-radius: 0.24rem;
  padding: 0.3rem 0.28rem;
  display: flex;
  align-items: center;
  box-shadow: 0 0.06rem 0.24rem rgba(31, 45, 61, 0.05);
  transition: all 0.16s ease;
}
.mis-trade-item.active {
  border-color: var(--accent, #2F54EB);
  box-shadow: 0 0.1rem 0.3rem rgba(47, 84, 235, 0.16);
}
.mis-trade-item:active { transform: scale(0.98); }
.mis-trade-icon {
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 0.22rem;
  transition: all 0.16s ease;
}
.mis-trade-icon >>> svg { width: 0.44rem; height: 0.44rem; }
.mis-trade-text { flex: 1; min-width: 0; }
.mis-trade-name { font-size: 0.34rem; font-weight: 700; color: #1f2329; }
.mis-trade-desc { font-size: 0.22rem; color: #9aa0a8; margin-top: 0.06rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mis-trade-check {
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 50%;
  border: 0.03rem solid #d5d8e0;
  flex-shrink: 0;
  margin-left: 0.12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: border-color 0.16s ease;
}
.mis-trade-check-dot { width: 0.18rem; height: 0.18rem; border-radius: 50%; }

/* 快捷金额 */
.mis-quick {
  display: flex;
  gap: 0.16rem;
  margin: 0.28rem 0.32rem 0;
}
.mis-quick-chip {
  flex: 1;
  height: 0.72rem;
  border-radius: 0.16rem;
  background: #fff;
  color: #2f4be0;
  font-size: 0.3rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0.04rem 0.14rem rgba(31, 45, 61, 0.05);
  transition: transform 0.08s ease, background 0.1s ease;
}
.mis-quick-chip:active { transform: scale(0.95); background: #eef2ff; }
.mis-quick-plus { font-size: 0.24rem; margin-right: 0.02rem; opacity: 0.7; }
.mis-quick-clear { color: #9aa0a8; font-weight: 600; }

/* 键盘 */
.mis-numpad { margin: 0.26rem 0.32rem 0; }
.mis-numpad-row { display: flex; gap: 0.18rem; margin-bottom: 0.18rem; }
.mis-numpad-key {
  flex: 1;
  height: 1.06rem;
  background: #fff;
  border-radius: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.56rem;
  font-weight: 700;
  color: #1f2329;
  box-shadow: 0 0.04rem 0.14rem rgba(31, 45, 61, 0.05);
  transition: transform 0.08s ease, background 0.1s ease;
}
.mis-numpad-key:active { background: #f0f2f7; transform: scale(0.97); }
.mis-numpad-key.is-del { color: #ff5a5f; }
.mis-numpad-key.is-del svg { width: 0.54rem; height: 0.54rem; }
.mis-numpad-key.is-dot { font-size: 0.7rem; }

/* 确认按钮 */
.mis-confirm {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.24rem 0.32rem calc(0.24rem + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 -0.04rem 0.2rem rgba(31, 45, 61, 0.06);
}
.mis-confirm-btn {
  height: 1.02rem;
  border-radius: 0.51rem;
  background: linear-gradient(135deg, #5b7bff 0%, #2f4be0 100%);
  color: #fff;
  font-size: 0.36rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0.1rem 0.28rem rgba(47, 75, 224, 0.4);
  transition: transform 0.1s ease;
}
.mis-confirm-btn.disabled { background: #c8ccd6; box-shadow: none; color: #fff; }
.mis-confirm-btn:active:not(.disabled) { transform: scale(0.99); }
.mis-confirm-amt { letter-spacing: 0.01rem; }

/* 通用弹层过渡 */
.mis-fade-enter-active, .mis-fade-leave-active { transition: opacity 0.2s ease; }
.mis-fade-enter, .mis-fade-leave-to { opacity: 0; }

/* 设备选择弹层 */
.mis-sheet-mask { position: fixed; inset: 0; z-index: 999; display: flex; align-items: flex-end; }
.mis-sheet-backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.5); }
.mis-sheet {
  position: relative;
  z-index: 1;
  width: 100%;
  background: #fff;
  border-radius: 0.32rem 0.32rem 0 0;
  padding-bottom: calc(0.32rem + env(safe-area-inset-bottom));
  max-height: 74vh;
  display: flex;
  flex-direction: column;
}
.mis-sheet-grip {
  width: 0.7rem;
  height: 0.08rem;
  border-radius: 0.04rem;
  background: #e2e5ec;
  margin: 0.2rem auto 0;
}
.mis-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.24rem 0.4rem 0.24rem;
  font-size: 0.34rem;
  font-weight: 700;
}
.mis-sheet-close { font-size: 0.52rem; color: #b4b9c2; line-height: 1; }
.mis-sheet-tip { padding: 0.9rem 0; text-align: center; color: #9aa0a8; font-size: 0.3rem; }
.mis-sheet-list { flex: 1; overflow-y: auto; padding: 0.1rem 0.32rem 0.1rem; }
.mis-sheet-item {
  display: flex;
  align-items: center;
  padding: 0.28rem;
  margin-bottom: 0.18rem;
  background: #f6f7fa;
  border: 0.03rem solid transparent;
  border-radius: 0.2rem;
}
.mis-sheet-item.active { background: #eef2ff; border-color: #2F54EB; }
.mis-sheet-item-main { flex: 1; min-width: 0; }
.mis-sheet-item-name { font-size: 0.32rem; font-weight: 600; color: #1f2329; }
.mis-sheet-item-sub { font-size: 0.26rem; color: #8a9099; margin-top: 0.08rem; word-break: break-all; }
.mis-sheet-item-radio {
  width: 0.36rem;
  height: 0.36rem;
  border-radius: 50%;
  border: 0.03rem solid #d0d3da;
  flex-shrink: 0;
  margin-left: 0.2rem;
  box-sizing: border-box;
}
.mis-sheet-item-radio.on { border-color: #2F54EB; border-width: 0.12rem; }
.mis-sheet-footer { padding: 0.24rem 0.32rem 0; }
.mis-sheet-confirm {
  height: 0.94rem;
  border-radius: 0.47rem;
  background: linear-gradient(135deg, #5b7bff 0%, #2f4be0 100%);
  color: #fff;
  font-size: 0.34rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mis-sheet-confirm:active { transform: scale(0.99); }

/* 收银台二维码弹层 */
.mis-qr-mask {
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.6rem;
}
.mis-qr-card {
  width: 100%;
  max-width: 6.6rem;
  background: #fff;
  border-radius: 0.32rem;
  padding: 0.56rem 0.5rem 0.44rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.mis-qr-title { font-size: 0.4rem; font-weight: 700; color: #1f2329; }
.mis-qr-sub { font-size: 0.26rem; color: #8a9099; margin-top: 0.12rem; }
.mis-qr-store { font-size: 0.32rem; font-weight: 600; color: #2f4be0; margin-top: 0.28rem; }
.mis-qr-img-wrap {
  margin-top: 0.28rem;
  padding: 0.28rem;
  background: #fff;
  border: 0.03rem solid #eef0f4;
  border-radius: 0.24rem;
  box-shadow: 0 0.08rem 0.3rem rgba(31, 45, 61, 0.08);
}
.mis-qr-img { width: 4.4rem; height: 4.4rem; display: block; }
.mis-qr-hint { font-size: 0.26rem; color: #9aa0a8; margin-top: 0.28rem; }
.mis-qr-close {
  margin-top: 0.4rem;
  width: 100%;
  height: 0.9rem;
  border-radius: 0.45rem;
  background: #f2f4f8;
  color: #4a5568;
  font-size: 0.32rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mis-qr-close:active { transform: scale(0.99); }

/* 收款结果遮罩 */
.mis-result-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.6rem;
}
.mis-result-box {
  width: 100%;
  max-width: 6.4rem;
  background: #fff;
  border-radius: 0.3rem;
  padding: 0.6rem 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.mis-result-spinner {
  width: 0.9rem;
  height: 0.9rem;
  border: 0.08rem solid #eef0f4;
  border-top-color: #2F54EB;
  border-radius: 50%;
  animation: mis-spin 0.8s linear infinite;
  margin-bottom: 0.32rem;
}
@keyframes mis-spin { to { transform: rotate(360deg); } }
.mis-result-icon {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 0.28rem;
}
.mis-result-icon svg { width: 0.6rem; height: 0.6rem; }
.mis-result-icon.success { background: #22c55e; box-shadow: 0 0.08rem 0.24rem rgba(34, 197, 94, 0.35); }
.mis-result-icon.fail { background: #ff4d4f; box-shadow: 0 0.08rem 0.24rem rgba(255, 77, 79, 0.3); }
.mis-result-title { font-size: 0.4rem; font-weight: 700; color: #1f2329; }
.mis-result-desc { font-size: 0.28rem; color: #8a9099; margin-top: 0.14rem; line-height: 1.5; }
.mis-result-amount { font-size: 0.64rem; font-weight: 800; color: #2f4be0; margin-top: 0.24rem; }
.mis-result-btn {
  margin-top: 0.44rem;
  width: 100%;
  height: 0.92rem;
  border-radius: 0.46rem;
  background: linear-gradient(135deg, #5b7bff 0%, #2f4be0 100%);
  color: #fff;
  font-size: 0.32rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mis-result-btn.ghost { background: #f2f4f8; color: #4a5568; margin-top: 0.36rem; }
.mis-result-btn:active { transform: scale(0.99); }
</style>
